import React, { Component } from 'react'
import { defineMessages, injectIntl } from 'react-intl'
import { inject, observer } from 'mobx-react'
import cx from 'classnames'

import { GateCard, Button } from '@/components'
import { Input } from '@/components/form'

import './InstructorAccessCodePage.scss'

const InstructorAccessCodePageMessages = defineMessages({
  accessCodePlaceholder: {
    defaultMessage: 'Enter your access code',
    description:
      'Placeholder for the input field to enter the instructor access code required to launch the course.',
    id: 'InstructorAccessCodePage.accessCodePlaceholder',
  },
  codeInvalid: {
    defaultMessage:
      'That access code is invalid. Please check the code with your instructor and try again.',
    description:
      'Error message displayed when the submitted instructor access code is not valid.',
    id: 'InstructorAccessCodePage.codeInvalid',
  },
  codeRequired: {
    defaultMessage:
      'The access code is required. Please enter an access code and try again.',
    description:
      'Error message displayed when the instructor access code field is left empty upon submission.',
    id: 'InstructorAccessCodePage.codeRequired',
  },
  continue: {
    defaultMessage: 'Continue',
    description: 'Button to submit the instructor access code.',
    id: 'InstructorAccessCodePage.continue',
  },
  info: {
    defaultMessage:
      'Your instructor has provided an access code for this course. Enter it below to continue.',
    description:
      'Instructional text shown on the instructor access code page below the title.',
    id: 'InstructorAccessCodePage.info',
  },
  logout: {
    defaultMessage: 'Log out',
    description:
      'Link on the instructor access code page allowing the user to log out instead of entering a code.',
    id: 'InstructorAccessCodePage.logout',
  },
  title: {
    defaultMessage: 'Enter your instructor access code',
    description: 'Title displayed on the instructor access code page.',
    id: 'InstructorAccessCodePage.title',
  },
})

@injectIntl
@inject('contentStore', 'sessionStore', 'uiStore')
@observer
export default class InstructorAccessCodePage extends Component {
  state = { code: '', codeError: null, hasChanged: false }
  componentDidMount() {
    const { uiStore } = this.props
    uiStore.updateTitle('Enter an Access Code')
  }
  onChange = (e) => {
    const { target } = e
    const { value } = target
    this.setState({ code: value, codeError: null, hasChanged: true })
  }
  onLogout = (e) => {
    e.preventDefault()
    const { sessionStore } = this.props
    sessionStore.logout()
  }
  onSubmit = (e) => {
    e.preventDefault()
    const { contentStore, intl, sessionStore } = this.props
    const { code } = this.state
    const { isVerifyingAccessCode } = sessionStore
    const { formatMessage } = intl
    if (isVerifyingAccessCode) return
    this.setState({ codeError: null })
    sessionStore
      .verifyInstructorAccessCode(code)
      .then(() => {
        // The gate has cleared. StudentLayout is already mounted, so its
        // componentDidMount (which skipped initCourse while gated) will not
        // run again — kick off the course load here, mirroring the same
        // course-picker guard used in StudentLayout.componentDidMount.
        const { session } = sessionStore
        const { hasMoreCourses, showCoursePicker } = session || {}
        if (!(hasMoreCourses && showCoursePicker)) {
          contentStore.initCourse()
        }
      })
      .catch(() => {
        // The backend error body shape is not guaranteed to be a string, so
        // always surface a friendly, localized message rather than raw data.
        this.setState({
          codeError: formatMessage(
            InstructorAccessCodePageMessages.codeInvalid,
          ),
        })
      })
  }
  renderForm = () => {
    const { intl, sessionStore } = this.props
    const { code, codeError, hasChanged } = this.state
    const { isVerifyingAccessCode } = sessionStore
    const { formatMessage } = intl
    const isEmpty = code === ''
    const hasError = (isEmpty && hasChanged) || codeError
    const helpClass = cx('help', {
      'is-danger': hasError,
    })
    return (
      <form onSubmit={this.onSubmit}>
        <div className="InstructorAccessCodePage__info">
          {formatMessage(InstructorAccessCodePageMessages.info)}
        </div>
        <div className="field is-grouped">
          <div className="control is-expanded">
            <Input
              isDanger={hasError}
              name="code"
              onChange={this.onChange}
              placeholder={formatMessage(
                InstructorAccessCodePageMessages.accessCodePlaceholder,
              )}
              value={code}
            />
            {hasError ? (
              <p className={helpClass}>
                {isEmpty
                  ? formatMessage(InstructorAccessCodePageMessages.codeRequired)
                  : codeError}
              </p>
            ) : null}
          </div>
          <div className="control">
            <Button
              className="InstructorAccessCodePage__submit"
              disabled={isEmpty || hasError || isVerifyingAccessCode}
              isLoading={isVerifyingAccessCode}
              isSuccess
              isWide
              submit
            >
              {formatMessage(InstructorAccessCodePageMessages.continue)}
            </Button>
          </div>
        </div>
        <div className="InstructorAccessCodePage__logout">
          <Button isText onClick={this.onLogout}>
            {formatMessage(InstructorAccessCodePageMessages.logout)}
          </Button>
        </div>
      </form>
    )
  }
  render() {
    const { intl } = this.props
    const { formatMessage } = intl
    return (
      <div className="InstructorAccessCodePage PageWrapper">
        <GateCard
          className="InstructorAccessCodePage__gate"
          title={formatMessage(InstructorAccessCodePageMessages.title)}
        >
          {this.renderForm()}
        </GateCard>
      </div>
    )
  }
}
